/**
 * Graphiques du HUD, en SVG et CSS, sans bibliothèque : ruban de 38 quarts
 * d'heure coloré par niveau de congestion, barres d'utilisation avec la
 * courbe d'attente, jauge circulaire, barre de charge et petite courbe.
 */
import {
  USAGE_BUCKETS,
  bucketLevel,
  seriesValue,
  type FacilityKind,
  type FacilitySeries,
} from '@snowchain/shared';
import { levelColor } from './labels';

/** Heures repérées sous les graphiques de la journée (plage 8 h à 17 h 30). */
const HOURS = [8, 10, 12, 14, 16];
const hourX = (h: number) => ((h - 8) * 4) / USAGE_BUCKETS;

export function HourAxis(props: { now?: number | null }) {
  return (
    <div class="g-axis" aria-hidden="true">
      {HOURS.map((h) => (
        <span key={h} style={{ left: `${hourX(h) * 100}%` }}>
          {h} h
        </span>
      ))}
      {props.now !== undefined && props.now !== null && props.now >= 0 && <i class="g-axis-now" style={{ left: `${((props.now + 0.5) / USAGE_BUCKETS) * 100}%` }} />}
    </div>
  );
}

/**
 * Ruban d'une installation : une case par quart d'heure, couleur du niveau
 * (vert fluide, jaune chargé, orange saturé, rouge bouchon, gris hors service).
 */
export function UsageStrip(props: {
  kind: FacilityKind;
  series: FacilitySeries | undefined;
  n: number;
  hover?: number | null;
  onHover?: (i: number | null) => void;
  onPick?: (i: number) => void;
  now?: number | null;
}) {
  const cells = [];
  for (let i = 0; i < USAGE_BUCKETS; i++) {
    const lv = i < props.n ? bucketLevel(props.kind, props.series, i) : null;
    const lost = i < props.n ? seriesValue(props.series, 'x', i) : -1;
    cells.push(
      <span
        key={i}
        class={`g-cell${lv === null ? ' g-cell-none' : ''}${props.hover === i ? ' g-cell-hover' : ''}${props.now === i ? ' g-cell-now' : ''}`}
        style={{ background: levelColor(lv) }}
        onMouseEnter={props.onHover ? () => props.onHover!(i) : undefined}
        onClick={props.onPick ? () => props.onPick!(i) : undefined}
      >
        {lost > 0 && <i class="g-cell-lost" />}
      </span>,
    );
  }
  return (
    <div class="g-strip" onMouseLeave={props.onHover ? () => props.onHover!(null) : undefined}>
      {cells}
    </div>
  );
}

/**
 * Barres d'utilisation par quart d'heure (couleur du niveau), courbe de
 * l'attente maximale et points rouges pour les clients perdus.
 */
export function UsageBars(props: {
  kind: FacilityKind;
  series: FacilitySeries | undefined;
  n: number;
  hover?: number | null;
  onHover?: (i: number | null) => void;
  now?: number | null;
}) {
  const W = 380;
  const H = 104;
  const top = 12;
  const base = 88;
  const bw = W / USAGE_BUCKETS;
  const s = props.series;
  let wMax = 60;
  for (let i = 0; i < props.n; i++) wMax = Math.max(wMax, seriesValue(s, 'w', i));
  const hasWait = !!s?.w;
  const bars = [];
  const wait: string[] = [];
  const lost = [];
  for (let i = 0; i < props.n; i++) {
    const u = seriesValue(s, 'u', i);
    if (u < 0) continue;
    const lv = bucketLevel(props.kind, s, i);
    const h = (Math.min(110, u) / 110) * (base - top);
    bars.push(<rect key={`b${i}`} x={i * bw + 1} y={base - Math.max(1.5, h)} width={bw - 2} height={Math.max(1.5, h)} rx={1.5} fill={lv !== null && lv < 0 ? 'rgba(255,255,255,0.14)' : levelColor(lv)} opacity={props.hover === null || props.hover === undefined || props.hover === i ? 1 : 0.55} />);
    if (hasWait) {
      const w = Math.max(0, seriesValue(s, 'w', i));
      wait.push(`${(i * bw + bw / 2).toFixed(1)},${(base - (w / wMax) * (base - top)).toFixed(1)}`);
    }
    const x = seriesValue(s, 'x', i);
    if (x > 0) lost.push(<circle key={`x${i}`} cx={i * bw + bw / 2} cy={6} r={Math.min(4.5, 2 + Math.log2(1 + x))} fill="var(--lv-3)" />);
  }
  return (
    <div class="g-bars" onMouseLeave={props.onHover ? () => props.onHover!(null) : undefined}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Utilisation par quart d'heure">
        {[0.25, 0.5, 0.75, 1].map((g) => (
          <line key={g} x1={0} x2={W} y1={base - (g / 1.1) * (base - top)} y2={base - (g / 1.1) * (base - top)} class={g === 1 ? 'g-grid-strong' : 'g-grid'} />
        ))}
        {bars}
        {wait.length > 1 && <polyline points={wait.join(' ')} class="g-wait-line" />}
        {lost}
        {HOURS.map((h) => (
          <text key={h} x={hourX(h) * W} y={H - 2} class="g-tick">
            {h} h
          </text>
        ))}
        {props.now !== undefined && props.now !== null && props.now >= 0 && <line x1={(props.now + 0.5) * bw} x2={(props.now + 0.5) * bw} y1={top - 4} y2={base} class="g-now-line" />}
        {props.onHover &&
          Array.from({ length: USAGE_BUCKETS }, (_, i) => (
            <rect key={`h${i}`} x={i * bw} y={0} width={bw} height={H} fill="transparent" onMouseEnter={() => props.onHover!(i)} />
          ))}
      </svg>
      {hasWait && (
        <div class="g-bars-legend">
          <span>
            <i class="g-key-bar" /> utilisation
          </span>
          <span>
            <i class="g-key-line" /> attente (max {wMax >= 60 ? `${Math.round(wMax / 60)} min` : `${wMax} s`})
          </span>
          <span>
            <i class="g-key-dot" /> clients perdus
          </span>
        </div>
      )}
    </div>
  );
}

/** Jauge circulaire de charge (0 à 100 %), couleur du niveau. */
export function Gauge(props: { value: number; level: number; label?: string; size?: number }) {
  const size = props.size ?? 76;
  const r = size / 2 - 6;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(1, props.value));
  return (
    <div class="g-gauge" style={{ width: `${size}px`, height: `${size}px` }}>
      <svg viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} class="g-gauge-track" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          class="g-gauge-fill"
          style={{ stroke: levelColor(props.level), strokeDasharray: `${c * v} ${c}` }}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span class="g-gauge-value num">
        {Math.round(Math.max(0, props.value) * 100)}
        <small>%</small>
      </span>
      {props.label && <span class="g-gauge-label">{props.label}</span>}
    </div>
  );
}

/** Barre de charge horizontale (0 à 100 %, au-delà marquée en rouge). */
export function LevelBar(props: { u: number; level: number; wide?: boolean }) {
  return (
    <span class={`g-lbar${props.wide ? ' g-lbar-wide' : ''}`}>
      <span style={{ width: `${Math.max(2, Math.min(1, props.u) * 100)}%`, background: levelColor(props.level) }} />
    </span>
  );
}

/** Petite courbe (stocks, fréquentation). Valeurs négatives ignorées. */
export function Sparkline(props: { values: number[]; max?: number; color?: string; height?: number; width?: number }) {
  const W = props.width ?? 120;
  const H = props.height ?? 28;
  const vals = props.values;
  const max = props.max ?? Math.max(1, ...vals);
  const n = Math.max(1, USAGE_BUCKETS - 1);
  const pts = vals
    .map((v, i) => (v < 0 ? null : `${((i / n) * W).toFixed(1)},${(H - 2 - (Math.min(max, v) / max) * (H - 4)).toFixed(1)}`))
    .filter(Boolean)
    .join(' ');
  return (
    <svg class="g-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={pts} style={{ stroke: props.color ?? 'var(--glacier-2)' }} />
    </svg>
  );
}
